import { useId, type CSSProperties, type ReactNode } from 'react'
import {
  CalendarX,
  Clock,
  Coins,
  FileText,
  Gauge,
  Hourglass,
  ListChecks,
  MicOff,
  Sparkles,
  Timer,
  X,
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, useIsDark } from '@highlight/ui'
import {
  planContinueClasses,
  planToken,
  SOLID_CTA_BORDER_CLASSES,
  type SelectableV2Plan,
} from '@renderer/components/pricing/plan-select-cards'
import { PLAN_MOSAICS, type MosaicPalette } from '@renderer/components/pricing/plan-mosaic'
import type { PlanBannerCopy, PlanBannerKind } from './plan-banner-rules'

/** The announcement banners share the layout but not the paywall rules. */
export type PlanBannerGlyph = PlanBannerKind | 'plan-change'

const ICONS: Record<PlanBannerGlyph, ReactNode> = {
  'plan-ended': <CalendarX />,
  'trial-ending': <Clock />,
  'trial-ended': <Hourglass />,
  'limits-out': <Gauge />,
  'hours-out': <MicOff />,
  'credits-out': <Coins />,
  'briefs-out': <FileText />,
  'follow-ups-out': <ListChecks />,
  'hours-low': <Timer />,
  'credits-low': <Coins />,
  'plan-change': <Sparkles />,
}

/** The hue's step-12 ink, not the plan's solid: a mark, not a fill. */
const GLYPH_INK: Record<ReturnType<typeof planToken>, string> = {
  brand: 'text-brand',
  'premium-green': 'text-success',
  'premium-blue': 'text-info',
}

const MOSAIC_CELL_PX = 6

const SURFACE = 'var(--background)'

/**
 * The torn foot: a wave 12px broad and 4px deep. The trough is cut from the
 * body and the bump added across the mid-line; `round` fits whole cycles.
 */
const TORN_EDGE_MASK: CSSProperties = {
  mask: [
    'radial-gradient(3px 2px at 75% 100%, #000 98%, #0000) 0 calc(100% - 2px) / 12px 2px round no-repeat',
    'radial-gradient(3px 2px at 25% 50%, #000 98%, #0000) 0 100% / 12px 4px round no-repeat',
    'linear-gradient(#000 0 0) 0 0 / 100% calc(100% - 2px) no-repeat',
  ].join(', '),
  maskComposite: 'exclude, add',
}

/**
 * The palette's lead washed into a neighbour, easing toward the panel at the
 * foot so the torn edge has paper to show. Mixed in oklab: the surface has no
 * hue, so an oklch mix would swing it.
 */
function washStyle([t0, , t2]: MosaicPalette): CSSProperties {
  return {
    background: [
      `linear-gradient(to bottom, transparent, color-mix(in oklab, ${SURFACE} 50%, transparent))`,
      `linear-gradient(115deg, color-mix(in oklab, ${t0} 20%, ${SURFACE}), color-mix(in oklab, ${t2} 30%, ${SURFACE}))`,
    ].join(', '),
  }
}

export interface PlanBannerProps {
  kind: PlanBannerGlyph
  copy: PlanBannerCopy
  /** The plan the action sells or refills. The wash, glyph and button wear its colour. */
  ctaPlan: SelectableV2Plan
  /** Runs the CTA. Absent, with no CTA copy, for a banner that only informs. */
  onAction?: () => void
  /** Runs the second action, with `copy.secondaryCta`. */
  onSecondaryAction?: () => void
  onDismiss: () => void
  className?: string
}

/**
 * One fact and one way on, at the top of the sidepanel home: a glyph naming
 * the kind of message, a title, a line, the action, and an X. Choosing a plan
 * or an amount happens in Settings, so the action only opens it. Purely
 * presentational, so every state renders from a story. Without a CTA the
 * action row is left out and the X is the only way on. Fades when given
 * `opacity-0`, so a caller can let it go before unmounting it.
 */
export function PlanBanner({
  kind,
  copy,
  ctaPlan,
  onAction,
  onSecondaryAction,
  onDismiss,
  className,
}: PlanBannerProps) {
  const isDark = useIsDark()
  const titleId = useId()
  const mosaic = PLAN_MOSAICS[ctaPlan]
  const palette = isDark ? mosaic.dark : mosaic.light

  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(
        'relative isolate flex flex-col gap-3 p-3.5 pb-5 transition-opacity duration-150 motion-reduce:transition-none',
        className,
      )}
      style={{ ...washStyle(palette), ...TORN_EDGE_MASK }}
    >
      <span
        aria-hidden="true"
        className={twMerge(
          'bg-background corner-squircle grid size-8 place-items-center rounded-xl shadow-[inset_0_0_0_var(--border-hairline)_var(--border)] [&_svg]:size-4',
          GLYPH_INK[planToken(ctaPlan)],
        )}
      >
        {ICONS[kind]}
      </span>
      {/* The 24px button puts its 14px glyph 14px from the top and the side. */}
      <Button
        variant="ghost"
        size="sm"
        shape="square"
        aria-label="Dismiss"
        title="Dismiss"
        className="text-foreground-placeholder hover:text-foreground absolute right-[9px] top-[9px]"
        onClick={onDismiss}
      >
        <X />
      </Button>
      <div>
        <h2 id={titleId} className="type-ui-md text-foreground m-0 font-semibold">
          {copy.title}
        </h2>
        <p className="type-prose-sm text-foreground-secondary m-0 mt-0.5 text-pretty">{copy.body}</p>
      </div>
      {(copy.cta || copy.stay) && (
        <div className="flex items-center gap-2">
          {copy.cta && (
            <Button
              size="md"
              className={twMerge(planContinueClasses(ctaPlan), SOLID_CTA_BORDER_CLASSES)}
              onClick={onAction}
            >
              {copy.cta}
            </Button>
          )}
          {copy.secondaryCta && (
            <Button variant="secondary" size="md" onClick={onSecondaryAction}>
              {copy.secondaryCta}
            </Button>
          )}
          {copy.stay && (
            <Button variant="secondary" size="md" onClick={onDismiss}>
              {copy.stay}
            </Button>
          )}
        </div>
      )}
      <PlanBannerMosaic grid={mosaic.grid} palette={palette} />
    </section>
  )
}

/** Inert and behind the content, its base level with the action's and its right edge on the content's. */
function PlanBannerMosaic({ grid, palette }: { grid: ReadonlyArray<string>; palette: MosaicPalette }) {
  const width = grid[0].length * MOSAIC_CELL_PX
  const height = grid.length * MOSAIC_CELL_PX
  return (
    <svg
      aria-hidden="true"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="pointer-events-none absolute bottom-5 right-3.5 -z-10 select-none"
    >
      {grid.flatMap((row, r) =>
        [...row].flatMap((cell, c) =>
          cell === '.'
            ? []
            : [
                <rect
                  key={`${r}-${c}`}
                  x={c * MOSAIC_CELL_PX + 0.5}
                  y={r * MOSAIC_CELL_PX + 0.5}
                  width={MOSAIC_CELL_PX - 1}
                  height={MOSAIC_CELL_PX - 1}
                  fill={palette[Number(cell)]}
                />,
              ],
        ),
      )}
    </svg>
  )
}
